import { useState, useEffect } from "react";
import { NAV_ITEMS } from "@/lib/index";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (href: string) => {
    setMobileOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-background/80 border-b border-border shadow-lg shadow-primary/5"
          : "bg-transparent"
      }`}
      style={{ backdropFilter: scrolled ? "blur(20px)" : "none" }}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-primary-foreground text-xs font-bold"
            style={{
              background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)",
              boxShadow: "0 0 12px color-mix(in srgb, var(--primary) 50%, transparent)",
            }}
          >
            R
          </div>
          <span className="font-bold text-foreground text-lg tracking-tight">
            Rokid <span className="text-primary">乐奇</span>
          </span>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.href}
              onClick={() => handleNav(item.href)}
              className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 font-medium"
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* CTA */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => handleNav("#buy")}
            className="px-5 py-2 rounded-full text-sm font-semibold text-primary-foreground transition-all duration-200 hover:scale-105"
            style={{
              background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)",
              boxShadow: "0 4px 15px color-mix(in srgb, var(--primary) 40%, transparent)",
            }}
          >
            立即购买
          </button>
        </div>

        {/* Mobile Menu Btn */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-background/95 border-b border-border px-6 py-4 flex flex-col gap-4"
            style={{ backdropFilter: "blur(20px)" }}
          >
            {NAV_ITEMS.map((item) => (
              <button
                key={item.href}
                onClick={() => handleNav(item.href)}
                className="text-left text-muted-foreground hover:text-primary transition-colors font-medium py-1"
              >
                {item.label}
              </button>
            ))}
            <button
              onClick={() => handleNav("#buy")}
              className="mt-2 px-5 py-2.5 rounded-full text-sm font-semibold text-primary-foreground w-full"
              style={{
                background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)",
              }}
            >
              立即购买
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-sidebar border-t border-border py-10 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center text-primary-foreground text-xs font-bold"
              style={{ background: "linear-gradient(135deg, var(--primary) 0%, var(--accent) 100%)" }}
            >
              R
            </div>
            <span className="font-bold text-foreground">Rokid 乐奇</span>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            © 2026 Rokid Inc. 杭州灵伴科技有限公司 版权所有
          </p>
          <p className="text-xs text-muted-foreground italic" style={{ color: "var(--primary)", opacity: 0.7 }}>
            Leave Nobody Behind · 让每个人享受科技
          </p>
        </div>
      </div>
    </footer>
  );
}
